/**
 * 加密货币加仓计算工具
 */
export class CryptoAddCalculator {
  /**
   * 公式1：计算加仓后的新成本价
   * @param P0 - 初始成本价
   * @param V - 当前持仓总量
   * @param Pc - 当前价格
   * @param X - 加仓金额
   * @param Pa - 加仓价格
   * @returns 新成本价
   */
  static calculateNewCost(P0: number, V: number, Pc: number, X: number, Pa: number): number {
    // 输入验证
    if (Pc <= 0 || Pa <= 0 || X < 0) {
      throw new Error('价格必须大于0，加仓金额不能为负');
    }

    const Q = V / Pc; // 当前持仓数量
    const C0 = Q * P0; // 初始总成本
    const C_new = C0 + X; // 新总成本
    const Q_new = Q + X / Pa; // 新总数量

    if (Q_new <= 0) {
      throw new Error('计算错误：新总数量为零或负值');
    }

    return C_new / Q_new;
  }

  /**
   * 公式2：已知加仓金额，计算需要的加仓价格
   * @param P0 - 初始成本价
   * @param V - 当前持仓总量
   * @param Pc - 当前价格
   * @param Pt - 目标成本价
   * @param X - 加仓金额
   * @returns 需要的加仓价格
   */
  static calculateAddPrice(P0: number, V: number, Pc: number, Pt: number, X: number): number {
    // 输入验证
    if (Pc <= 0 || X <= 0) {
      throw new Error('当前价格和加仓金额必须大于0');
    }
    
    // 计算当前持仓数量
    const Q = V / Pc;
    
    // 计算公式: (C0 + X) / (Q + X/Pa) = Pt
    // C0 + X = Pt * Q + Pt * X / Pa
    // C0 + X - Pt * Q = Pt * X / Pa
    // Pa = (Pt * X) / (C0 + X - Pt * Q)
    
    const C0 = P0 * Q;
    const numerator = Pt * X;
    const denominator = C0 + X - Pt * Q;

    if (denominator <= 0) {
       // 这意味着即使以无穷大的价格加仓，也无法将成本拉高/拉低到目标价
       // 或者分母为负，意味着目标价设定不合理
       throw new Error('无法通过该加仓金额达到目标成本价（分母<=0）');
    }

    const Pa = numerator / denominator;

    if (Pa <= 0) {
      throw new Error('计算出的加仓价格不合理(<=0)');
    }

    return Pa;
  }

  /**
   * 公式3：已知加仓价格，计算需要的加仓金额
   * @param P0 - 初始成本价
   * @param V - 当前持仓总量
   * @param Pc - 当前价格
   * @param Pt - 目标成本价
   * @param Pa - 加仓价格
   * @returns 需要加仓的金额
   */
  static calculateAddAmount(P0: number, V: number, Pc: number, Pt: number, Pa: number): number {
    // 输入验证
    if (Pc <= 0 || Pa <= 0) {
      throw new Error('价格必须大于0');
    }
    
    // 计算当前持仓数量
    const Q = V / Pc;
    const C0 = P0 * Q;
    
    // 计算公式: (C0 + X) / (Q + X/Pa) = Pt
    // C0 + X = Pt * Q + Pt * X / Pa
    // X - Pt * X / Pa = Pt * Q - C0
    // X * (1 - Pt/Pa) = Pt * Q - C0
    // X = (Pt * Q - C0) / (1 - Pt/Pa)
    
    const numerator = Pt * Q - C0;
    const denominator = 1 - Pt / Pa;
    
    if (denominator === 0) {
       throw new Error('目标成本价不能等于加仓价格');
    }
    
    const X = numerator / denominator;

    if (X <= 0) {
      throw new Error('计算出的加仓金额不合理(<=0)，请检查目标成本价是否合理');
    }

    return X;
  }

  /**
   * 获取当前持仓信息
   * @param P0 - 初始成本价
   * @param V - 当前持仓总量
   * @param Pc - 当前价格
   * @returns 持仓信息
   */
  static getPositionInfo(P0: number, V: number, Pc: number) {
    const Q = V / Pc; // 当前持仓数量
    const C0 = Q * P0; // 初始总成本
    const profit = V - C0; // 当前盈亏
    const profitRate = (profit / C0) * 100; // 盈亏百分比

    return {
      quantity: Q, // 持仓数量
      initialCost: C0, // 初始总成本
      currentValue: V, // 当前总价值
      profit: profit, // 当前盈亏
      profitRate: profitRate, // 盈亏百分比
      currentPrice: Pc, // 当前价格
    };
  }

  /**
   * 计算盈亏百分比
   * @param currentPrice - 当前价格
   * @param targetPrice - 目标价格
   * @returns 盈亏百分比（相对于目标价格的盈亏）
   */
  static calculateProfitRate(currentPrice: number, targetPrice: number): number {
    if (targetPrice <= 0) {
      throw new Error('目标价格必须大于0');
    }
    // 计算相对于目标价格的盈亏百分比
    // 如果当前价格 > 目标价格，则为盈利（正数）
    // 如果当前价格 < 目标价格，则为亏损（负数）
    return ((currentPrice - targetPrice) / targetPrice) * 100;
  }
}
